<template>
  <div class="export-experiments">
    <!-- Filtering Navbar -->
    <nav class="filter-navbar">
      <div class="filter-group">
        <label>Projects:</label>
        <multiselect
          v-model="selectedProjects"
          :options="projects"
          :multiple="true"
          :close-on-select="false"
          placeholder="Select Projects"
          label="name"
          track-by="id"
        >
          <template #selection="{ values, isOpen }">
            <span v-if="values.length" v-show="!isOpen">{{ values.length }} selected</span>
          </template>
        </multiselect>
      </div>

      <div class="filter-group">
        <label>Locations:</label>
        <multiselect
          v-model="selectedLocations"
          :options="locations"
          :multiple="true"
          :close-on-select="false"
          placeholder="Select Locations"
          label="name"
          track-by="id"
        >
          <template #selection="{ values, isOpen }">
            <span v-if="values.length" v-show="!isOpen">{{ values.length }} selected</span>
          </template>
        </multiselect>
      </div>

      <div class="filter-group">
        <label>Workflows:</label>
        <multiselect
          v-model="selectedWorkflows"
          :options="workflows"
          :multiple="true"
          :close-on-select="false"
          placeholder="Select Workflows"
          label="name"
          track-by="id"
        >
          <template #selection="{ values, isOpen }">
            <span v-if="values.length" v-show="!isOpen">{{ values.length }} selected</span>
          </template>
        </multiselect>
      </div>

      <div class="filter-group">
        <label>Instruments:</label>
        <multiselect
          v-model="selectedInstruments"
          :options="instruments"
          :multiple="true"
          :close-on-select="false"
          placeholder="Select Instruments"
          label="name"
          track-by="id"
        >
          <template #selection="{ values, isOpen }">
            <span v-if="values.length" v-show="!isOpen">{{ values.length }} selected</span>
          </template>
        </multiselect>
      </div>

      <div class="filter-group">
        <label>Start Date:</label>
        <input type="date" v-model="startDate" class="filter-input" />
      </div>

      <div class="filter-group">
        <label>End Date:</label>
        <input type="date" v-model="endDate" class="filter-input" />
      </div>

      <button @click="fetchFilteredExperiments" class="filter-button">
        Fetch Data
      </button>
    </nav>

    <!-- Table Display -->
    <div v-if="experiments.length" class="table-container">
      <table class="experiment-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Project</th>
            <th>Location</th>
            <th>Workflow</th>
            <th>Action</th>
            <th>Start Date</th>
            <th>End Date</th>

            <!-- Dynamic Metadata Sections -->
            <th v-for="(mdlist, mdlistId) in metadataKeys" :key="'section-' + mdlistId" :colspan="mdlist.keys.length">
              {{ mdlist.name }}
            </th>
          </tr>
          <tr>
            <th colspan="7"></th> <!-- Placeholder for fixed columns -->

            <template v-for="(mdlist, mdlistId) in metadataKeys" :key="'headers-' + mdlistId">
              <th v-for="tableId in mdlist.keys" :key="'key-' + tableId" class="metadata-column">
                <span class="tooltip-container">
                  <span class="tooltip-text">{{ getTableHeader(tableId) }}</span>
                </span>
              </th>
            </template>
          </tr>
        </thead>
        <tbody>
          <tr v-for="experiment in paginatedExperiments" :key="experiment.id">
            <td>{{ experiment.name }}</td>
            <td>{{ experiment.project }}</td>
            <td>{{ experiment.location }}</td>
            <td>{{ experiment.workflow }}</td>
            <td>{{ experiment.action }}</td>
            <td>{{ formatDate(experiment.start_date) }}</td>
            <td>{{ formatDate(experiment.end_date) }}</td>

            <!-- Corrected dynamic metadata values -->
            <template v-for="(mdlist, mdlistId) in metadataKeys" :key="'values-' + mdlistId">
              <td v-for="tableId in mdlist.keys" :key="'exp-' + experiment.id + '-' + tableId">
                {{ experiment.metadata[mdlistId]?.values?.[tableId] || '—' }}
              </td>
            </template>
          </tr>
        </tbody>
      </table>
    </div>
    <!-- Pagination Controls -->
    <div v-if="experiments.length" class="pagination-controls">
        <button @click="prevPage" :disabled="currentPage === 1">Previous</button>
        <span>Page {{ currentPage }} of {{ totalPages }}</span>
        <button @click="nextPage" :disabled="currentPage >= totalPages">Next</button>
      </div>

      <div v-if="experiments.length" class="export-container">
        <select v-model="selectedFormat" class="export-dropdown">
          <option value="xlsx">Excel (.xlsx)</option>
          <option value="csv">CSV (.csv)</option>
          <option value="json">JSON</option>
        </select>
        <button @click="exportData" class="export-button">Export</button>
      </div>

  </div>
</template>

<script>
import axios from "axios";
import "vue-multiselect/dist/vue-multiselect.css";
import Multiselect from "vue-multiselect";
import { API_ENDPOINTS } from "@/services/config.js";
import { useToast } from "vue-toastification";
import * as XLSX from "xlsx";
import { mapState } from 'vuex';


export default {
  components: { Multiselect },
  data() {
    return {
      projects: [],
      locations: [],
      workflows: [],
      instruments: [],
      experiments: [],
      tableDict: {},  // Table dictionary mapping table_id -> key/unit
      metadataKeys: {},

      selectedProjects: [],
      selectedLocations: [],
      selectedWorkflows: [],
      selectedInstruments: [],
      startDate: "",
      endDate: "",
      toast: useToast(),

      currentPage: 1,
      pageSize: 20,
      selectedFormat: "xlsx",
    };
  },
  async mounted() {
    await this.fetchFilterOptions();
  },
  computed: {
    ...mapState(['loggedInUser']),
    paginatedExperiments() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.experiments.slice(start, start + this.pageSize);
    },
    totalPages() {
      return Math.ceil(this.experiments.length / this.pageSize);
    },
  },
  methods: {
    async fetchFilterOptions() {
      try {
        const [projectsRes, locationsRes, workflowsRes, instrumentsRes] = await Promise.all([
          axios.get(API_ENDPOINTS.projects, {
          params: {
            entra_id: this.loggedInUser.entra_id,
          },
        }),
          axios.get(API_ENDPOINTS.locations),
          axios.get(API_ENDPOINTS.workflows, {
          params: {
            entra_id: this.loggedInUser.entra_id,
          },
        }),
          axios.get(API_ENDPOINTS.allInstruments),
        ]);

        this.projects = projectsRes.data;
        this.locations = locationsRes.data;
        this.workflows = workflowsRes.data;
        this.instruments = instrumentsRes.data;
      } catch (error) {
        console.error("Error fetching filter options:", error);
        this.toast.error("Failed to load filter options.");
      }
    },

    async fetchFilteredExperiments() {
      try {
        const response = await axios.post(API_ENDPOINTS.filteredExperiments, {
          project_ids: this.selectedProjects.map(p => p.id),
          location_ids: this.selectedLocations.map(l => l.id),
          workflow_ids: this.selectedWorkflows.map(w => w.id),
          instrument_ids: this.selectedInstruments.map(i => i.id),
          start_date: this.startDate,
          end_date: this.endDate,
          entra_id: this.loggedInUser.entra_id
        });

        this.tableDict = response.data.table_dict;
        this.experiments = response.data.experiments;
        this.extractMetadataKeys();
      } catch (error) {
        console.error("Error fetching experiments:", error);
        this.toast.error("Failed to fetch experiments.");
      }
    },

    extractMetadataKeys() {
      const mdKeys = {};

      this.experiments.forEach(exp => {
        if (exp.metadata) {
          Object.entries(exp.metadata).forEach(([mdlistId, mdlistData]) => {
            if (!mdKeys[mdlistId]) {
              mdKeys[mdlistId] = { name: mdlistData.name, keys: new Set() };
            }

            Object.keys(mdlistData.values).forEach(tableId => {
              mdKeys[mdlistId].keys.add(tableId);
            });
          });
        }
      });

      this.metadataKeys = Object.fromEntries(
        Object.entries(mdKeys).map(([id, data]) => [id, { name: data.name, keys: Array.from(data.keys) }])
      );
    },

    getTableHeader(tableId) {
      const entry = this.tableDict[tableId];
      return entry ? `${entry.key} (${entry.unit})` : `Unknown (${tableId})`;
    },

    async exportData() {
      if (!this.experiments.length) {
        this.toast.error("No data to export!");
        return;
      }

      console.log("Selected export format:", this.selectedFormat); // Debugging log

      if (this.selectedFormat === "csv") {
        this.exportToCSV();
      } else if (this.selectedFormat === "xlsx") {
        this.exportToExcel();
      } else if (this.selectedFormat === "json") {
        await this.exportToJson();
      } else {
        console.error("Invalid export format selected.");
        this.toast.error("Invalid export format selected.");
      }
    },


    async exportToJson() {
      try {
        console.log("exportToJson")
        const response = await axios.post(API_ENDPOINTS.filteredExperiments, {
          project_ids: this.selectedProjects.map(p => p.id),
          location_ids: this.selectedLocations.map(l => l.id),
          workflow_ids: this.selectedWorkflows.map(w => w.id),
          instrument_ids: this.selectedInstruments.map(i => i.id),
          start_date: this.startDate,
          end_date: this.endDate,
          normalized: false 
        });

        const jsonData = JSON.stringify(response.data, null, 2); 

        // Create a downloadable JSON file
        const blob = new Blob([jsonData], { type: "application/json" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "experiments_export.json";
        link.click();

        this.toast.success("JSON file exported successfully!");
      } catch (error) {
        console.error("Error exporting JSON:", error);
        this.toast.error("Failed to export JSON.");
      }
    },

    exportToExcel() {
      if (!this.experiments.length) {
        this.toast.error("No data to export!");
        return;
      }

      // Define headers
      const headers = [
        "Name", "Project", "Location", "Workflow", "Action", "Start Date", "End Date"
      ];

      // Add dynamic metadata column headers
      Object.values(this.metadataKeys).forEach(mdlist => {
        mdlist.keys.forEach(tableId => {
          headers.push(this.getTableHeader(tableId)); // Get readable key/unit
        });
      });

      // Prepare data rows
      const rows = this.experiments.map(exp => {
        let row = [
          exp.name, exp.project, exp.location, exp.workflow, exp.action,
          exp.start_date, exp.end_date
        ];

        // Add metadata values in correct order
        Object.entries(this.metadataKeys).forEach(([mdlistId, mdlist]) => {
          mdlist.keys.forEach(tableId => {
            const value = exp.metadata?.[mdlistId]?.values?.[tableId] ?? "—";
            row.push(value);
          });
        });


        return row;
      });

      // Combine headers and rows
      const worksheetData = [headers, ...rows];

      // Create a worksheet
      const ws = XLSX.utils.aoa_to_sheet(worksheetData);

      // Create a workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Experiments");

      // Trigger file download
      XLSX.writeFile(wb, "experiments_export.xlsx");
    },

    exportToCSV() {
      if (!this.experiments.length) {
        this.toast.error("No data to export!");
        return;
      }

      // Define headers
      const headers = [
        "Name", "Project", "Location", "Workflow", "Action", "Start Date", "End Date"
      ];

      // Add dynamic metadata column headers
      Object.values(this.metadataKeys).forEach(mdlist => {
        mdlist.keys.forEach(tableId => {
          headers.push(this.getTableHeader(tableId));
        });
      });

      // Prepare data rows
      const rows = this.experiments.map(exp => {
        let row = [
          exp.name, exp.project, exp.location, exp.workflow, exp.action,
          exp.start_date, exp.end_date
        ];

        // Add metadata values
        Object.entries(this.metadataKeys).forEach(([mdlistId, mdlist]) => {
          mdlist.keys.forEach(tableId => {
            const value = exp.metadata?.[mdlistId]?.values?.[tableId] ?? "—";
            row.push(value);
          });
        });

        return row;
      });

      // Convert to CSV format and prepend UTF-8 BOM for Excel
      const csvContent = [
        "\uFEFF" + headers.join(","), // UTF-8 BOM ensures proper character display
        ...rows.map(row => row.map(value => `"${value}"`).join(",")) // Quote each value
      ].join("\n");

      // Create and trigger file download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "experiments_export.csv";
      link.click();
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB");
    },
    prevPage() {
      if (this.currentPage > 1) this.currentPage--;
    },

    nextPage() {
      if (this.currentPage < this.totalPages) this.currentPage++;
    },
  }
};
</script>


<style scoped lang="scss">
@import "@/assets/variables.scss";
// General Styling
.export-experiments {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;

  .filter-navbar {
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
    align-items: center; 
    justify-content: center; 
    background: #f8f9fa;
    padding: 15px;
    border-radius: 8px;
    box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.1);
    text-align: center;
  }


.multiselect {
  width: 200px !important; 
  min-width: 200px !important;
  max-width: 200px !important;
}


.multiselect__content-wrapper {
  width: 100% !important;
  min-width: 200px !important;
  max-width: 200px !important;
}

  .filter-group {
    display: flex;
    flex-direction: column;
    gap: 5px;
    align-items: center;
    text-align: center;
  }


  // Experiment Table
  .table-container {
    overflow-x: auto;
    background: white;
    border-radius: 8px;
    box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.1);
    padding: 10px;

    .experiment-table {
      width: max-content;
      min-width: 100%;
      border-collapse: collapse;
      margin: auto;

      th,
      td {
        padding: 10px;
        text-align: left;
        border-bottom: 1px solid #ddd;
      }

      th {
        background: #f1f1f1;
      }
    }
  }
  .export-container {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 10px;
  }

  .export-dropdown {
    padding: 5px;
    border: 1px solid #ccc;
    border-radius: 5px;
    cursor: pointer;
  }

  // Export Button
  .export-button {
    margin-top: 10px;
    padding: 8px 16px;
    color: white;
    font-weight: bold;
    border: none;
    border-radius: 5px;
    cursor: pointer;
  }
  .pagination-controls {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 10px;
}

.pagination-controls button {
  padding: 5px 10px;
  border: none;
  cursor: pointer;
}
}
</style>
  